/**
 * The page tour's pure rules (SA-1315, spec §3, §5): which tour a page has, what a
 * reveal may open, and where the ring and the hole sit. No DOM writes, no store.
 */
import { matchPath } from 'react-router-dom'
import type { TourStep } from '@/content/docs/frontmatter'
import { RECORDS_TOKEN, type TaskTour } from './taskTour'

/** What the engine needs from a docs entry; the registry's `DocEntry` fits. */
export interface TourGuide {
  slug: string
  title: string
  routes?: string[]
  tour?: TourStep[]
}

export interface PageTour {
  /** The guide's file name, `inbox` for `pages/inbox`: what the feedback ring records. */
  id: string
  title: string
  steps: TourStep[]
}

/** The tour of the visible page guide whose routes match `pathname`, else null. */
export function tourFor(pathname: string, guides: readonly TourGuide[]): PageTour | null {
  for (const guide of guides) {
    if (!guide.tour || guide.tour.length === 0 || !guide.routes) continue
    if (guide.routes.some((route) => matchPath({ path: route, end: true }, pathname))) {
      return { id: guide.slug.split('/').pop() ?? guide.slug, title: guide.title, steps: guide.tour }
    }
  }
  return null
}

export type RevealAction = 'expand' | 'details' | 'tab'

/**
 * How a reveal anchor may be opened, or null when it is not something meant to open.
 * A tour never presses an action button for the person (spec §3): only a disclosure
 * trigger that says it is collapsed, a closed `<details>`, or an unselected tab.
 */
export function revealAction(el: Element): RevealAction | null {
  if (el.tagName === 'DETAILS') return (el as HTMLDetailsElement).open ? null : 'details'
  if (el.getAttribute('role') === 'tab') return el.getAttribute('aria-selected') === 'true' ? null : 'tab'
  if (el.getAttribute('aria-expanded') === 'false') return 'expand'
  return null
}

export interface Box {
  top: number
  left: number
  width: number
  height: number
}

/** The ring's padding outside the target, in px. */
export const RING_PAD = 8

/**
 * The ring (and the scrim's hole) around a target: padded by `RING_PAD`, clamped to the
 * viewport below `headerInset` (a fixed or sticky header) so it never draws under the bar.
 */
export function placeRing(target: Box, viewport: { width: number; height: number }, headerInset = 0): Box {
  const top = Math.max(headerInset, target.top - RING_PAD)
  const left = Math.max(0, target.left - RING_PAD)
  const bottom = Math.min(viewport.height, target.top + target.height + RING_PAD)
  const right = Math.min(viewport.width, target.left + target.width + RING_PAD)
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) }
}

/** Either kind of tour the engine plays: a page guide's, or a walkthrough section's (SA-1316). */
export type AnyTour = PageTour | TaskTour

export function isTaskTour(tour: AnyTour): tour is TaskTour {
  return 'kind' in tour && tour.kind === 'task'
}

/** `watch` is a page tour's step; the other three are a task tour's (SA-1316 spec §8). */
export type PlayedMode = 'watch' | 'go' | 'do' | 'read'

/** One step as the engine plays it, whichever kind of tour it came from. */
export interface PlayedStep {
  mode: PlayedMode
  /** What to ring: a tour anchor, or a nav row by its route; null on a read step. */
  selector: string | null
  reveal?: string
  text: string
  /** The missing state's words. */
  whenMissing?: string
  /** "What you'll see". */
  expect?: string
  /** The on-screen word a do step's person presses. */
  name?: string
  /** The routes a go step completes on. */
  routes?: string[]
}

const attr = (name: string, value: string) => `[${name}="${value.replace(/["\\]/g, '')}"]`

/** The step at `index` of `tour`, with the deployment's record noun in a go step's words. */
export function playedStep(tour: AnyTour, index: number, recordsPlural: string): PlayedStep | null {
  if (!isTaskTour(tour)) {
    const s = tour.steps[index]
    if (!s) return null
    return { mode: 'watch', selector: attr('data-tour', s.anchor), reveal: s.reveal, text: s.text, whenMissing: s.when_missing }
  }
  const s = tour.steps[index]
  if (!s) return null
  const noun = (text: string) => text.split(RECORDS_TOKEN).join(recordsPlural)
  if (s.mode === 'read') return { mode: 'read', selector: null, text: s.text, expect: s.expect }
  if (s.mode === 'go') {
    const title = noun(s.title ?? '')
    return {
      mode: 'go',
      selector: s.nav ? attr('data-tour-nav', s.nav) : s.anchor ? attr('data-tour', s.anchor) : null,
      text: noun(s.text),
      whenMissing: goMissing(title),
      routes: s.routes ?? [],
    }
  }
  return {
    mode: 'do',
    selector: s.anchor ? attr('data-tour', s.anchor) : null,
    text: s.text,
    expect: s.expect,
    name: s.name,
    whenMissing: s.name ? doMissing(s.name) : undefined,
  }
}

/** The missing words of a go step whose nav row is not on screen (a collapsed menu, a phone). */
export const goMissing = (title: string) => `Open ${title} from the menu, then press Next.`
/** The missing words of a do step whose control is not on the page. */
export const doMissing = (name: string) => `${name} shows only when it applies here.`

/** Whether `pathname` is one of `routes` (React Router patterns). */
export function onRoute(routes: readonly string[], pathname: string): boolean {
  return routes.some((route) => matchPath({ path: route, end: true }, pathname) !== null)
}
